import type { ProjectKind } from '../data/types';

/**
 * One-liner "what's actually happening" tips that rotate while a model
 * trains (and after, while the kid stares at the loss curve). The
 * waiting time is the teaching window — these turn the progress bar
 * into a tiny ML class. Tone: friendly, accurate, never patronizing.
 * Each line should read in roughly 4–6 seconds.
 *
 * Phase buckets (selected via `getTrainingTips(kind, phase)`):
 *   * `'extracting'` — MobileNet (or the audio CNN) is running every
 *     sample through to produce embeddings. Kids see "Looking at your
 *     samples (X / Y)". Tips here narrate feature extraction.
 *   * `'training'` — the actual gradient-descent loop on the head
 *     layer. Tips here explain weights, loss, gradients, softmax,
 *     backprop, batches, epochs. The "from amazing to learning" tier.
 *   * `'post'` — the model is trained. Tips here explain how to read
 *     the loss curve and the accuracy number, and what generalization
 *     means.
 *
 * Add freely. The rotation shuffles without replacement, so the size
 * of a pool only affects how often a tip repeats across runs.
 */

export type TipPhase = 'extracting' | 'training' | 'post';

const universal: readonly string[] = [
  'Right now, the model is looking at every example you gave it — over and over.',
  'Each time it gets one wrong, it nudges itself a tiny bit toward the right answer.',
  "It isn't memorizing. It's hunting for the pattern your examples share.",
  'More variety in your samples = a smarter model. Different angles. Different light. Different backgrounds.',
  'It will never know more than what you showed it. That is why your samples matter.',
  'If two of your classes look or sound alike, the model will tell you — by getting confused.',
  'Training is guess → check → adjust, repeated thousands of times a second.',
  "Confidence isn't truth. The model can be confidently wrong. Worth knowing.",
  'Wrong predictions are clues. They tell you what your samples are missing.',
  "The model isn't 'thinking'. It's pattern-matching, very fast.",
  'If your samples are biased, your model will be too. It is a mirror of what you fed it.',
  'Real ML labs train for hours or days. You are getting a tiny taste of the same thing.',
  'Show it something it has never seen and it will still pick one of your classes. It only knows your world.',
  'Wrong predictions are interesting, not failures. They tell you something true about your data.',
  "A model is never 'done learning'. It just gets good enough to be useful.",
  "If you trained your friend's voice, it might not work for your sister. That's a feature, not a bug.",
];

const universalMath: readonly string[] = [
  'Inside the model are numbers called weights. Training is the act of nudging those numbers.',
  "A weight says: 'when I see this feature, push the answer toward that class — by this much.'",
  "Every guess gets a single score: loss. One number that asks 'how wrong was I?'",
  'Lower loss = better model. The whole job of training is making that number go down.',
  'The gradient is an arrow that points downhill on the loss. The model follows it.',
  'Step size matters. Too big and it overshoots. Too small and it never gets there. That is the learning rate.',
  'Backpropagation is how the blame for a wrong guess flows backwards through the model.',
  'All this math has a name: calculus. The model takes derivatives — millions of them per second.',
  'Each class gets a raw score called a logit. Softmax turns those into percentages that add to 100%.',
  "A '93% cat' came from softmax. It is a confidence number, not a fact.",
  'The loss formula here is called cross-entropy. Confident-and-wrong hurts more than meek-and-wrong.',
  'A batch is a small group of samples. The model studies a batch, learns, then grabs the next.',
  'One full trip through every sample is called an epoch. We do many.',
  'A neuron is just: multiply inputs by weights, add them up, run through a tiny gate.',
  "That gate is called ReLU — 'if positive, keep it; if not, zero.' Surprisingly powerful.",
  'Every weight starts as a random number. Training turns randomness into pattern.',
  'There are no rules in the model. Just numbers that, together, behave like rules.',
  'The optimizer here is called Adam. It remembers the direction it has been heading and uses that.',
  'Train too long and the model starts memorizing exact samples. That is called overfitting.',
  "We hide some of your samples from training, then test on them. That's how we know it's really learning.",
  'A neural network is just a chain of multiplications and additions. Stacked deep, they get clever.',
  'Each layer takes numbers in and gives different numbers out. Patterns get more abstract as you go deeper.',
  'Two numbers tell you almost everything: loss going down, accuracy going up. Watch them together.',
  'If loss zigzags wildly, your learning rate is probably too big. Smaller step, smoother ride.',
  'The gradient is computed by the chain rule from calculus. The same rule you will learn in school one day.',
  'A model with more weights can learn more — but also memorize faster. Bigger is not always better.',
  "The 'parameters' you hear about? Same thing as weights. Just a fancier word.",
  'Forward pass: predict. Backward pass: learn. Both happen, batch after batch, until the loss settles.',
  'If accuracy looks too perfect, the test set was probably too easy. Be suspicious.',
  'A model can be 99% accurate and still wrong on the things you most need it to get right.',
];

const image: readonly string[] = [
  'Pictures, to the model, are just millions of brightness numbers — three for each pixel.',
  "It's noticing edges, textures, and colors — not what things technically are.",
  'Lighting can fool it. Train with samples from different times of day.',
  'If every sample of a class was on your desk, the model might be learning your desk.',
  "If you only showed your hand from one angle, it won't recognize the other.",
  'Your camera quality matters. Blurry samples = blurry instincts.',
  "It does not know what 'a cat' is. It knows what your cat photos look like.",
  'Mirror your hand sometimes. The model treats left and right as totally different until you show it both.',
  "If the lighting changes from training to test, the model can fail in surprising ways. That's normal.",
];

const imageMath: readonly string[] = [
  'MobileNet — the brain already inside — was trained on 1.2 million photos before you arrived.',
  'MobileNet has about 4 million weights. We freeze them. They never change.',
  'We are only training a tiny extra layer on top — usually just a few thousand weights.',
  'That trick has a name: transfer learning. Stand on the shoulders of a model that already learned a lot.',
  'MobileNet turns your photo into a list of about 1,000 numbers — an embedding.',
  'Two pictures the model sees as similar will have embeddings pointing in similar directions.',
  'A photo is a 224×224 grid of colors before the model sees it. Resized first, every time.',
  "The model doesn't see 'red' or 'fluffy'. It sees stacks of edges, then stacks of corners, then stacks of shapes.",
  'Convolutions are the trick. Tiny filters slide across the image looking for specific patterns.',
  'Early layers find edges. Middle layers find textures. Deep layers find objects. Real, observed fact.',
];

const audio: readonly string[] = [
  'Before the model listens, your sound becomes a picture — a chart of how it looks.',
  "It's reading the shape of the sound, not the word inside it.",
  'Background noise during recording = background noise the model expects forever.',
  "Short, long, soft, loud — variety teaches it what 'clap' really means.",
  'Silence has a shape too. The model is learning what nothing sounds like.',
  'It cannot always tell two voices apart. Voices are surprisingly similar to a model.',
  'A whisper and a shout of the same word can look very different on a spectrogram.',
];

const audioMath: readonly string[] = [
  'Your sound becomes a spectrogram — a chart of which frequencies were loud, when.',
  'Time runs left to right. Frequencies stack low to high. Patterns show up like fingerprints.',
  'The clip gets chopped into windows about 25 milliseconds long.',
  'Each window becomes a column in the spectrogram. The model reads it like a tiny picture book.',
  'The audio model has its own pre-trained brain — trained on hundreds of thousands of voice clips.',
  'Frequencies are measured in hertz. A piano middle-C is about 262 Hz. A clap is messy across many.',
  "We use a 'mel scale' that matches how human ears hear pitch. The model thinks the way you hear.",
  'A 1-second audio clip turns into a tiny image: about 200 pixels tall, 96 wide. That is what the model studies.',
];

const pose: readonly string[] = [
  'The model is not looking at you. It is looking at 17 dots placed on your body.',
  'Your face, your clothes, the room — invisible. Just the skeleton.',
  "That's why pose works in any lighting and any outfit.",
  'It is comparing angles and distances between joints, like a tiny geometry judge.',
  'If half your body is off-screen, the keypoints get unsure. The model will say so.',
];

const poseMath: readonly string[] = [
  'The pose model finds 17 keypoints. Each one is just three numbers: x, y, and confidence.',
  'Your model trains on those numbers — about 51 of them per frame. Tiny compared to a photo.',
  'Distance between joints + angles = the whole story. That is it.',
  'We use a model called MoveNet. It looks at the picture once and finds the joints in one shot.',
  'The keypoints are: nose, eyes, ears, shoulders, elbows, wrists, hips, knees, ankles. Seventeen in all.',
];

// Phase: 'extracting' — MobileNet / audio-CNN feature extraction. The
// first phase the kid sees ("Looking at your samples X / Y"). One-time
// work that produces the embeddings the head layer will train on.

const extracting: readonly string[] = [
  'Right now, a big pre-trained brain is studying each sample for the first time.',
  'Each sample becomes a list of about a thousand numbers. Those numbers — not the originals — go into training.',
  'This is one-time work. After this, training is much faster than going back to the raw samples.',
  'The pre-trained brain is asking, "what does this look like to me?" — for every single sample.',
  'We freeze the pre-trained brain. It does not learn from your samples. It just summarizes them.',
  'Think of it like prepping ingredients before cooking. The model is chopping.',
  'These number-lists are called embeddings. Two samples that look or sound alike get similar embeddings.',
  'We do this once and reuse it across every training round. That is why training itself flies by.',
  'The original samples never leave your device. Only the number-summaries are passed along.',
  'If two of your classes have very similar embeddings, the next phase is going to struggle to tell them apart.',
];

const extractingImage: readonly string[] = [
  'Each photo is being squeezed down to 224×224 pixels first. Same input, every time.',
  'MobileNet runs each photo through 17 layers before it spits out the embedding.',
  'The early layers are catching edges. The middle ones, textures. The last ones, shapes that look like things.',
  'The output is a vector of 1,024 numbers per photo. That is what the head layer will train on.',
];

const extractingAudio: readonly string[] = [
  'Each audio clip is becoming a spectrogram first — a tiny picture of the sound.',
  'Then a small audio-trained brain turns that picture into a number-list.',
  "It's the same idea as image features, but the 'photos' here are sounds drawn as charts.",
];

const extractingPose: readonly string[] = [
  'For pose, the model is finding 17 keypoints on every frame. Each keypoint = three numbers: x, y, confidence.',
  'No embedding step here — your model trains directly on the keypoint vectors.',
];

// Phase: 'post' — the model is trained. Kid is looking at the loss
// curve + accuracy. Tips explain how to read those numbers honestly.

const post: readonly string[] = [
  'The loss curve shows how the model’s mistakes shrank over time. Lower = fewer / smaller mistakes.',
  'If the curve is still falling at the end, the model probably wants more rounds.',
  'A bumpy loss curve is normal at the start. It should smooth out by the end.',
  'If the curve flattens early at a high value, something is off. Maybe two of your classes overlap.',
  'Accuracy here is measured on samples we hid from training. That is what makes it honest.',
  'If accuracy is suspiciously high — like 100% — your test set was probably too easy.',
  'Accuracy is not everything. A model can be 95% accurate and still wrong on the cases that matter most.',
  'Loss is unitless. Lower = better. The shape of the curve tells you more than the final number.',
  'Two models with the same accuracy can fail in totally different ways. Test, do not just trust.',
  'If you train again on the same data, the curve will look slightly different. That is randomness in batching.',
  'The gap between training accuracy and test accuracy is called the generalization gap. Smaller is better.',
  'A flat-line loss from the start usually means the learning rate was too small. Bump it up.',
  'A loss that explodes upward means the learning rate was too big. Bring it down.',
  'If accuracy started high and dropped, the model may have started memorizing. That is overfitting.',
  'The model has a "best moment" partway through training. After that, more training can hurt.',
  'Honest test = samples the model never saw. Anything else is the model marking its own homework.',
];

const postImage: readonly string[] = [
  'Try it on something you never trained on. That is the real test.',
  'If your photos were all from one room, expect surprises in another room. Lighting, angle, background — they all matter.',
];

const postAudio: readonly string[] = [
  'Try it with background noise that was not in training. That is the real test.',
  'A whisper of the same word can sound very different to the model than a shout. Train on both.',
];

const postPose: readonly string[] = [
  'Try the pose at a different distance from the camera. Keypoint angles should still match.',
  'If half your body was off-screen during training, expect the model to wobble when it is fully visible.',
];

export function getTrainingTips(
  kind: ProjectKind,
  phase: TipPhase = 'training',
): readonly string[] {
  if (phase === 'extracting') {
    switch (kind) {
      case 'image':
        return [...extracting, ...extractingImage];
      case 'audio':
        return [...extracting, ...extractingAudio];
      case 'pose':
        return [...extracting, ...extractingPose];
    }
  }
  if (phase === 'post') {
    switch (kind) {
      case 'image':
        return [...post, ...postImage];
      case 'audio':
        return [...post, ...postAudio];
      case 'pose':
        return [...post, ...postPose];
    }
  }
  // Default: 'training' — the gradient-descent loop.
  switch (kind) {
    case 'image':
      return [...universal, ...universalMath, ...image, ...imageMath];
    case 'audio':
      return [...universal, ...universalMath, ...audio, ...audioMath];
    case 'pose':
      return [...universal, ...universalMath, ...pose, ...poseMath];
  }
}
